import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { IFilterProps } from './Filter';
import { normalizeFilters } from './normalizeFilters';

type Selections = { [childKey: string]: string[] };

interface IFilterGroupContextValue {
  childFilters: { [childKey: string]: IFilterProps };
  orderedChildFilters: [string, IFilterProps][];
  isVisible: boolean;
  context?: Record<string, any>;
  activeChildKey: string | null;
  setActiveChildKey: (key: string) => void;
  draftSelections: Selections;
  setChildSelection: (childKey: string, selected: string[]) => void;
  commitSelections: () => void;
  clearSelections: () => void;
}

const FilterGroupContext = createContext<IFilterGroupContextValue | undefined>(
  undefined
);

interface IProviderProps {
  childFilters: { [childKey: string]: IFilterProps };
  isVisible: boolean;
  context?: Record<string, any>;
  initialSelections: Selections;
  onApply: (selections: Selections) => void;
  onClear: () => void;
  children: React.ReactNode;
}

export const FilterGroupProvider: React.FC<IProviderProps> = ({
  childFilters,
  isVisible,
  context,
  initialSelections,
  onApply,
  onClear,
  children,
}) => {
  const orderedChildFilters = useMemo<[string, IFilterProps][]>(
    () => normalizeFilters(childFilters).map(child => [child.key, child]),
    [childFilters]
  );

  const [activeChildKey, setActiveChildKey] = useState<string | null>(
    orderedChildFilters[0]?.[0] || null
  );
  const [draftSelections, setDraftSelections] = useState<Selections>(
    initialSelections || {}
  );

  const setChildSelection = useCallback(
    (childKey: string, selected: string[]) => {
      setDraftSelections(prev => ({ ...prev, [childKey]: selected }));
    },
    []
  );

  const commitSelections = useCallback(() => {
    onApply(draftSelections);
  }, [draftSelections, onApply]);

  const clearSelections = useCallback(() => {
    setDraftSelections({});
    onClear();
  }, [onClear]);

  return (
    <FilterGroupContext.Provider
      value={{
        childFilters,
        orderedChildFilters,
        isVisible,
        context,
        activeChildKey,
        setActiveChildKey,
        draftSelections,
        setChildSelection,
        commitSelections,
        clearSelections,
      }}
    >
      {children}
    </FilterGroupContext.Provider>
  );
};

export const useFilterGroupContext = () => {
  const context = useContext(FilterGroupContext);
  if (!context) {
    throw new Error(
      'useFilterGroupContext must be used within a FilterGroupProvider'
    );
  }
  return context;
};
